import { useRef, useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dafava3ep053ijub1mu0";

const topics = ["提供爆料线索", "指正内容/纠错", "商务合作", "转载授权", "其他咨询"];

type Status = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLen, setMessageLen] = useState(0);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError("");
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾：蜜罐命中时静默返回成功，不向服务端提交
    const honeypot = String(fd.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessageLen(0);
      return;
    }

    const message = String(fd.get("message") || "").trim();
    if (!message) {
      setStatus("error");
      setFormError("请填写正文内容后再提交。");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "contact_alt" && String(value).trim()) {
        payload.append(key, String(value));
      }
    });

    setStatus("loading");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(
        parsed?.message || meta?.message || meta?.detail || (parsed?.meta && String((parsed.meta as Record<string, unknown>).detail)) || ""
      );
      const code = parsed?.code ? String(parsed.code) : "";
      const looksSpam = /spam|form data is spam/i.test(responseText) || /spam/i.test(serverMsg);

      if (res.ok && code === "OK" && !looksSpam) {
        setStatus("success");
        form.reset();
        setMessageLen(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  }

  return (
    <section id="contact" className="scroll-mt-28 border-t border-background-200 bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold uppercase tracking-widest text-primary-600">Contact</span>
            <h2 className="mt-2 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              你有线索？我们等一个求证
            </h2>
            <p className="mt-3 text-sm leading-8 text-foreground-600">
              无论是提供独家线索、指正我们内容里的疏漏，还是洽谈合作转载，都欢迎来信。编辑部会对每条线索做信源核验，能追到源头的内容才会被纳入深扒。
            </p>

            <ul className="mt-7 space-y-3">
              <li className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-100/60 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className="ri-mail-send-line h-5 w-5"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">投稿与线索</p>
                  <a href="mailto:tougao@tangxin-chigua.example" className="text-sm text-foreground-600 hover:text-primary-600">
                    tougao@tangxin-chigua.example
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-100/60 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                  <i className="ri-time-line h-5 w-5"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">响应时效</p>
                  <p className="text-sm text-foreground-600">工作日 48 小时内回复，重大线索优先处理</p>
                </div>
              </li>
              <li className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-100/60 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-800">
                  <i className="ri-shield-check-line h-5 w-5"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">隐私承诺</p>
                  <p className="text-sm text-foreground-600">线索人信息严格保密，未经同意不对外披露</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              ref={formRef}
              onSubmit={onSubmit}
              data-readdy-form="吃瓜糖心投稿联系"
              noValidate
              className="rounded-2xl border border-background-200 bg-background-100/50 p-5 md:p-7"
            >
              <div className="form-honeypot-wrap" aria-hidden="true">
                <label htmlFor="contact_alt">不要填写此栏</label>
                <input id="contact_alt" name="contact_alt" type="text" tabIndex={-1} autoComplete="off" readOnly aria-hidden="true" />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    怎么称呼你
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={20}
                    placeholder="例如：小林"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    maxLength={60}
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    来信类型
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    defaultValue={topics[0]}
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    主题 / 关键词
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="一句话概括"
                    className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="mb-1.5 flex items-center justify-between text-sm font-medium text-foreground-800">
                  <span>正文内容 / 线索描述</span>
                  <span className={`text-xs ${messageLen >= 500 ? "text-primary-600" : "text-foreground-400"}`}>{messageLen}/500</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  onChange={(e) => setMessageLen(e.target.value.length)}
                  placeholder="请尽量描述事件背景、已知信源与你掌握的线索。越具体，越便于我们核验与跟进。"
                  className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line h-4 w-4 animate-spin"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill h-4 w-4"></i>
                      提交给编辑部
                    </>
                  )}
                </button>
                <p className="text-xs text-foreground-500">提交即表示同意我们仅将信息用于信源核验，不作它用。</p>
              </div>

              {status === "success" && (
                <div role="status" className="mt-4 flex items-start gap-2 rounded-lg border border-accent-200 bg-accent-100/70 p-3.5 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-fill h-5 w-5 shrink-0"></i>
                  <span>已收到你的来信，感谢信任。我们会在工作日 48 小时内与你联系。</span>
                </div>
              )}
              {status === "error" && formError && (
                <div role="alert" className="mt-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-100/70 p-3.5 text-sm text-primary-800">
                  <i className="ri-error-warning-fill h-5 w-5 shrink-0"></i>
                  <span>{formError}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}